import {BetaLabel} from '@github-ui/lifecycle-labels/beta'
import {useCollapsibleSidebarContext} from '@github-ui/layout-primitives/CollapsibleSidebar'
import {CollapsibleNavList} from '@github-ui/layout-primitives/CollapsibleNavList'
import {SidebarTooltip} from '@github-ui/layout-primitives/SidebarTooltip'
import {noop} from '@github-ui/noop'
import {ExtendedLink, useLocation} from '@github-ui/react-router'
import type {SidebarLink} from '@github-ui/saved-views'
import {SidebarList} from '@github-ui/shared-dashboard-sidebar/SidebarList'
import {softNavigate} from '@github-ui/soft-navigate'
import {useAnalytics} from '@github-ui/use-analytics'
import {MilestoneIcon, StackIcon, TableIcon, TagIcon} from '@primer/octicons-react'
import {NavList} from '@primer/react'
import {clsx} from 'clsx'
import type {ComponentType, MouseEvent, ReactElement, ReactNode} from 'react'
import {useCallback, useEffect, useMemo, useRef} from 'react'

import {repoSidebarLinks} from './repos-issues-sidebar-links'
import {useIssuesSidebarState} from './use-issues-sidebar-state'
import {getRepoIssuesQueryPath} from './urls'
import styles from './ReposIssuesSidebar.module.css'
import {useFeedbackLink} from './CollapsibleFeedbackLink'

/** Shared analytics identifier for the repo-scoped issues sidebar. */
const DASHBOARD_TYPE = 'repo-issues'

export interface ReposIssuesSidebarProps {
  owner: string
  repo: string
  forceHardNavigation?: boolean
  /**
   * When true, renders the collapsible icon-rail UI. Pass this from a caller
   * that wraps the sidebar in a `<CollapsibleSidebar>` provider — e.g.
   * `<RepoIssuesPageLayout>` or `<CollapsiblePartialSidebar>`. Defaults to
   * `false` (legacy fixed-width sidebar).
   */
  collapsible?: boolean
  /**
   * Whether the repository has the Projects feature enabled in its settings.
   * When false, the "Projects" link is hidden from the sidebar. Defaults to
   * false so the link is hidden if the server doesn't provide this value.
   */
  hasProjectsEnabled?: boolean
}

/**
 * Repo-scoped navigation sidebar for the Issues, Milestones and Labels pages.
 *
 * When `collapsible` is `false` (legacy callers), renders the legacy sidebar
 * that is visually identical to `main` (BetaLabel footer, original CSS).
 *
 * When `collapsible` is `true` (callers wrapping in `<CollapsibleSidebar>`),
 * branches between an expanded tree (Primer NavList) and a condensed icon
 * rail. The collapse state is read from `useCollapsibleSidebarContext()`
 * which defaults to `isExpanded: true` when no provider is present.
 */
export function ReposIssuesSidebar({
  owner,
  repo,
  forceHardNavigation = false,
  collapsible = false,
  hasProjectsEnabled = false,
}: ReposIssuesSidebarProps) {
  // The `collapsible` prop is the single source of truth: callers that
  // wrap us in a `<CollapsibleSidebar>` provider pass `collapsible` so we
  // render the icon-rail UI; all other callers get the legacy fixed sidebar.
  const isCollapsible = collapsible
  const {pathname} = useLocation()
  const {selectedLink} = useIssuesSidebarState()
  const repoPath = `/${owner}/${repo}`
  const labelsPath = `${repoPath}/labels`
  const issuesPath = `${repoPath}/issues`
  const projectsPath = `${repoPath}/projects`
  const milestonesPath = `${repoPath}/milestones`
  const milestonePath = `${repoPath}/milestone/`
  const viewsPath = `${issuesPath}/views`

  // Track sidebar impressions so we can measure the ratio of users who
  // see the sidebar vs. those who click a link.
  const {sendAnalyticsEvent} = useAnalytics()
  const impressionSentRef = useRef(false)
  useEffect(() => {
    if (!impressionSentRef.current) {
      impressionSentRef.current = true
      sendAnalyticsEvent(`${DASHBOARD_TYPE}.view`, 'SIDEBAR_IMPRESSION')
    }
  }, [sendAnalyticsEvent])

  const handleNavClick = useCallback(
    (id: string) => () => {
      sendAnalyticsEvent(`${DASHBOARD_TYPE}.click`, 'SIDEBAR_LINK', {id})
    },
    [sendAnalyticsEvent],
  )

  const linksWithPaths = useMemo(
    () =>
      repoSidebarLinks.map(link => ({
        ...link,
        path: getRepoIssuesQueryPath({owner, repo, query: link.query}),
      })),
    [owner, repo],
  )

  const feedbackUrl = useFeedbackLink()
  const {isInDialog} = useCollapsibleSidebarContext()

  // Shared nav-item config — both the legacy `<NavList>` and the new
  // `<CollapsibleNavList>` render from this single source of truth.
  const pageItems: PageNavItemConfig[] = [
    {
      id: 'views',
      path: viewsPath,
      icon: StackIcon,
      label: 'Views',
      forceHardNavigation,
      isCurrent: pathname === viewsPath || pathname.startsWith(`${viewsPath}/`),
    } as const,
    ...(hasProjectsEnabled
      ? [
          {
            id: 'projects',
            path: projectsPath,
            icon: TableIcon,
            label: 'Projects',
            forceHardNavigation: false,
            isCurrent: pathname === projectsPath,
          } as const,
        ]
      : []),
    {
      id: 'milestones',
      path: milestonesPath,
      icon: MilestoneIcon,
      label: 'Milestones',
      forceHardNavigation,
      isCurrent: pathname.startsWith(milestonesPath) || pathname.startsWith(milestonePath),
    },
    {
      id: 'labels',
      path: labelsPath,
      icon: TagIcon,
      label: 'Labels',
      forceHardNavigation,
      isCurrent: pathname === labelsPath || pathname === `${issuesPath}/labels`,
    },
  ]

  // ── Legacy sidebar (flag OFF) ──────────────────────────────────────
  if (!isCollapsible) {
    return (
      <LegacySidebar
        linksWithPaths={linksWithPaths}
        selectedLink={selectedLink}
        issuesPath={issuesPath}
        pageItems={pageItems}
        forceHardNavigation={forceHardNavigation}
        handleNavClick={handleNavClick}
        feedbackUrl={feedbackUrl}
      />
    )
  }

  // ── Collapsible sidebar (flag ON) ──────────────────────────────────
  return (
    // Landmark is owned by the wrapping component (CollapsibleSidebar /
    // SidebarPageLayout.Sidebar / PageLayout.Pane).
    <div className={clsx(styles.sidebar, styles.sidebarCollapsible)} data-in-dialog={isInDialog ? '' : undefined}>
      <SidebarList
        aria-label="Issue filters"
        dashboardType={DASHBOARD_TYPE}
        links={linksWithPaths}
        selectedLink={selectedLink}
        onSelectLink={noop}
        generateLinkPath={path => path ?? issuesPath}
        forceHardNavigation={forceHardNavigation}
        ItemWrapper={CollapsibleFilterItemWrapper}
      />

      <hr className={styles.divider} />

      <CollapsibleNavList aria-label="Pages">
        {pageItems.map(item => (
          <CollapsibleNavList.LinkItem
            key={item.id}
            to={item.path}
            icon={item.icon}
            label={item.label}
            forceHardNavigation={item.forceHardNavigation}
            aria-current={item.isCurrent ? 'page' : undefined}
            onClick={handleNavClick(item.id)}
          />
        ))}
      </CollapsibleNavList>
    </div>
  )
}

/**
 * ── Legacy sidebar (flag OFF) ──────────────────────────────────────────
 *
 * Renders the exact same JSX as the `main` branch so the flag-OFF path
 * is visually identical. Uses `SidebarNavItem` + `BetaLabel`.
 */
function LegacySidebar({
  linksWithPaths,
  selectedLink,
  issuesPath,
  pageItems,
  forceHardNavigation,
  handleNavClick,
  feedbackUrl,
}: {
  linksWithPaths: Array<SidebarLink & {path: string}>
  selectedLink: string
  issuesPath: string
  pageItems: PageNavItemConfig[]
  forceHardNavigation: boolean
  handleNavClick: (id: string) => () => void
  feedbackUrl: string
}) {
  return (
    // Landmark is owned by the wrapping component.
    <div className={styles.sidebar}>
      <SidebarList
        aria-label="Issue filters"
        dashboardType={DASHBOARD_TYPE}
        links={linksWithPaths}
        selectedLink={selectedLink}
        onSelectLink={noop}
        generateLinkPath={path => path ?? issuesPath}
        forceHardNavigation={forceHardNavigation}
      />

      <hr className={styles.divider} />

      <NavList aria-label="Pages">
        {pageItems.map(item => (
          <SidebarNavItem
            key={item.id}
            aria-current={item.isCurrent && 'page'}
            path={item.path}
            forceHardNavigation={item.forceHardNavigation}
            icon={item.icon}
            onClick={handleNavClick(item.id)}
          >
            {item.label}
          </SidebarNavItem>
        ))}
      </NavList>

      <BetaLabel className={styles.betaLabel} feedbackUrl={feedbackUrl} />
    </div>
  )
}

/**
 * NavList.Item wrapper used by the legacy (flag-OFF) sidebar. Uses polymorphic
 * `as` prop internally. NavList.LeadingVisual must be a direct child of
 * NavList.Item (enforced by primer-react/direct-slot-children lint rule), so
 * JSX is repeated in each branch.
 */
function SidebarNavItem({
  path,
  forceHardNavigation,
  'aria-current': ariaCurrent,
  icon: Icon,
  onClick,
  children,
}: {
  path: string
  forceHardNavigation: boolean
  'aria-current': false | 'page'
  icon: ComponentType
  onClick?: () => void
  children: ReactNode
}) {
  const handleClick = useCallback(
    (e: MouseEvent) => {
      onClick?.()
      if (forceHardNavigation) {
        e.preventDefault()
        softNavigate(path)
      }
    },
    [path, onClick, forceHardNavigation],
  )

  if (forceHardNavigation) {
    return (
      <NavList.Item aria-current={ariaCurrent} href={path} onClick={handleClick}>
        <NavList.LeadingVisual>
          <Icon />
        </NavList.LeadingVisual>
        {children}
      </NavList.Item>
    )
  }
  return (
    <NavList.Item aria-current={ariaCurrent} as={ExtendedLink} to={path} onClick={handleClick}>
      <NavList.LeadingVisual>
        <Icon />
      </NavList.LeadingVisual>
      {children}
    </NavList.Item>
  )
}

/*
 * ── Collapsible sidebar helpers (flag ON) ─────────────────────────────
 */

/**
 * ItemWrapper for SidebarList that shows a tooltip on hover/focus in
 * condensed mode. Reads expanded state from context so it can be a
 * stable top-level component reference.
 */
function CollapsibleFilterItemWrapper({linkItem, children}: {linkItem: {name: string}; children: ReactElement}) {
  const {visualIsExpanded} = useCollapsibleSidebarContext()

  if (visualIsExpanded) return children

  return <SidebarTooltip text={linkItem.name}>{children}</SidebarTooltip>
}

type PageNavItemConfig = {
  id: string
  path: string
  icon: ComponentType<{size?: number; className?: string}>
  label: string
  forceHardNavigation: boolean
  isCurrent: boolean
}
